import {
  FloatingFocusManager,
  FloatingNode,
  FloatingOverlay,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import {
  type CSSProperties,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { SharedAppLayoutBottomSheetProps } from '../types';

import {
  FloatingPriorityRoot,
  useEscapePriorityDismiss,
} from '../../../utils/floatingDismissPriority';
import './BottomSheet.css';

const ANIMATION_MS = 240;
const DISMISS_DISTANCE = 96;
const DISMISS_VELOCITY = 0.5;
const DRAG_START_DISTANCE = 4;
const SCRIM_FADE_DISTANCE = 400;

function BottomSheetContent({
  children,
  dismissLabel,
  isOpen,
  onClose,
  label,
  root,
}: SharedAppLayoutBottomSheetProps) {
  const nodeId = useFloatingNodeId();
  const { refs, context } = useFloating({
    nodeId,
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) onClose();
    },
  });
  const role = useRole(context, { role: 'dialog' });
  const dismiss = useEscapePriorityDismiss(context, { outsidePress: false });
  const { getFloatingProps } = useInteractions([role, dismiss]);

  const [isRendered, setIsRendered] = useState(isOpen);
  const [isActive, setIsActive] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [renderedChildren, setRenderedChildren] = useState<ReactNode>(children);

  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartTimeRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setRenderedChildren(children);
    }
  }, [isOpen, children]);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      return;
    }
    const timeout = setTimeout(() => setIsRendered(false), ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (!isRendered || !isOpen) {
      setIsActive(false);
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setIsActive(true);
        setDragOffset(0);
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [isRendered, isOpen]);

  const endDrag = useCallback(
    (clientY: number) => {
      if (!isDraggingRef.current) {
        return;
      }
      isDraggingRef.current = false;
      const distance = Math.max(0, clientY - dragStartYRef.current);
      const elapsed = Date.now() - dragStartTimeRef.current || 1;
      const velocity = distance / elapsed;
      if (distance > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY) {
        onClose();
      } else {
        setDragOffset(0);
      }
    },
    [onClose],
  );

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    isDraggingRef.current = true;
    dragStartYRef.current = event.clientY;
    dragStartTimeRef.current = Date.now();
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!isDraggingRef.current) {
      return;
    }
    const offset = Math.max(0, event.clientY - dragStartYRef.current);
    if (offset >= DRAG_START_DISTANCE) {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    setDragOffset(offset);
  }

  function handlePointerEnd(event: ReactPointerEvent<HTMLDivElement>) {
    endDrag(event.clientY);
  }

  if (!isRendered) {
    return null;
  }

  const style = {
    '--_animation-ms': `${ANIMATION_MS}ms`,
    '--_scrim-opacity': isActive
      ? Math.max(0, 1 - dragOffset / SCRIM_FADE_DISTANCE)
      : 0,
    '--_sheet-transform': `translateY(${isActive ? `${dragOffset}px` : '100%'})`,
    '--_sheet-transition': isDraggingRef.current
      ? 'none'
      : `transform ${ANIMATION_MS}ms ease`,
  } as CSSProperties;

  return createPortal(
    <FloatingNode id={nodeId}>
      <FloatingOverlay
        lockScroll
        className="eds-app-layout-bottom-sheet"
        style={style}
      >
        <div
          aria-hidden="true"
          className="eds-app-layout-bottom-sheet--scrim"
          onClick={onClose}
        />
        <FloatingFocusManager
          context={context}
          disabled={!isOpen}
          initialFocus={refs.floating}
        >
          <div
            {...getFloatingProps()}
            ref={refs.setFloating}
            aria-label={label}
            aria-modal="true"
            className="eds-app-layout-bottom-sheet--dialog"
            tabIndex={-1}
          >
            <div
              className="eds-app-layout-bottom-sheet--handle"
              onPointerCancel={handlePointerEnd}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
            >
              <button
                aria-label={dismissLabel}
                className="eds-app-layout-bottom-sheet--dismiss"
                type="button"
                onClick={onClose}
              >
                <span
                  aria-hidden="true"
                  className="eds-app-layout-bottom-sheet--grabber"
                />
              </button>
            </div>
            <div
              aria-label={label}
              className="eds-app-layout-bottom-sheet--body"
              role="region"
              // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- overflow region must be keyboard-focusable (eds-constitution §4)
              tabIndex={0}
            >
              {isOpen ? children : renderedChildren}
            </div>
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingNode>,
    root ?? document.body,
  );
}

export const BottomSheet = memo(function BottomSheet(
  props: SharedAppLayoutBottomSheetProps,
) {
  return (
    <FloatingPriorityRoot>
      <BottomSheetContent {...props} />
    </FloatingPriorityRoot>
  );
});

BottomSheet.displayName = 'AppLayoutBottomSheet';
